import {COUNTRIES} from "@/store/constants";
import type {TCountry} from "@/store/types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import * as RPNInput from "react-phone-number-input";
import de from "react-phone-number-input/locale/de.json";
import en from "react-phone-number-input/locale/en.json";

type TCountryData = {
  code: string;
  countryCode: string;
  image: string;
  mask: string;
  name: TCountry;
  nationality: string;
};

export const countriesData: {
  [key in TCountry]: TCountryData;
} = {
  Switzerland: {
    name: "Switzerland",
    code: "CH",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f3/Flag_of_Switzerland.svg/50px-Flag_of_Switzerland.svg.png",
    countryCode: "+41",
    mask: "## ### ## ##",
    nationality: "Swiss",
  },
  Germany: {
    name: "Germany",
    code: "DE",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Flag_of_Germany.svg/50px-Flag_of_Germany.svg.png",
    countryCode: "+49",
    mask: "### ########",
    nationality: "German",
  },
  Austria: {
    name: "Austria",
    code: "AT",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/Flag_of_Austria.svg/50px-Flag_of_Austria.svg.png",
    countryCode: "+43",
    mask: "###### ########",
    nationality: "Austrian",
  },
  France: {
    name: "France",
    code: "FR",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Flag_of_France.svg/50px-Flag_of_France.svg.png",
    countryCode: "+33",
    mask: "# ## ## ## ##",
    nationality: "French",
  },
  Italy: {
    name: "Italy",
    code: "IT",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/03/Flag_of_Italy.svg/50px-Flag_of_Italy.svg.png",
    countryCode: "+39",
    mask: "### ########",
    nationality: "Italian",
  },
  Liechtenstein: {
    name: "Liechtenstein",
    code: "LI",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/Flag_of_Liechtenstein.svg/50px-Flag_of_Liechtenstein.svg.png",
    countryCode: "+423",
    mask: "### ## ##",
    nationality: "Liechtensteiner",
  },
  Luxembourg: {
    name: "Luxembourg",
    code: "LU",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/d/da/Flag_of_Luxembourg.svg/50px-Flag_of_Luxembourg.svg.png",
    countryCode: "+352",
    mask: "### ### ###",
    nationality: "Luxembourgish",
  },
  "United States": {
    name: "United States",
    code: "US",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Flag_of_the_United_States.svg/50px-Flag_of_the_United_States.svg.png",
    countryCode: "+1",
    mask: "### ### ####",
    nationality: "American",
  },
  "United Kingdom": {
    name: "United Kingdom",
    code: "GB",
    image:
      "https://upload.wikimedia.org/wikipedia/en/thumb/a/ae/Flag_of_the_United_Kingdom.svg/50px-Flag_of_the_United_Kingdom.svg.png",
    countryCode: "+44",
    mask: "#### ######",
    nationality: "British",
  },
} as const;

const useCountries = () => {
  const [allCountries, setAllCountries] = useState<
    {
      value: RPNInput.Country;
      label: string;
      enLabel: string;
    }[]
  >();
  const {i18n} = useTranslation("common");

  useEffect(() => {
    const currentLang = i18n.resolvedLanguage;

    if (currentLang) {
      const locales: Record<string, de | en> = {
        en,
        ger: de,
      };

      const localeDefault = locales[currentLang] || en;

      const countries = RPNInput.getCountries().map(code => ({
        value: code,
        label: localeDefault[code],
        enLabel: en[code],
      }));

      setAllCountries(countries);
    }
  }, [i18n.resolvedLanguage]);

  const getCountryTranslatedLabelByCode = (code: RPNInput.Country) =>
    allCountries?.find(country => country.value === code)?.label ?? "Switzerland";

  const getCountryCodeByEnLabelOrUndefined = (label?: string | null) =>
    allCountries?.find(country => country.enLabel === label)?.value;

  const getCountryCodeByEnLabel = (label?: string | null) => getCountryCodeByEnLabelOrUndefined(label) ?? "CH";

  const getCountryEnLabelByTranslatedLabel = (label?: string | null) =>
    allCountries?.find(country => country.label === label)?.enLabel ?? "Switzerland";

  const getCountryEnLabelByCode = (code: RPNInput.Country) =>
    allCountries?.find(country => country.value === code)?.enLabel ?? "Switzerland";

  return {
    countries: COUNTRIES,
    countriesData,
    allCountries,
    getCountryTranslatedLabelByCode,
    getCountryCodeByEnLabel,
    getCountryCodeByEnLabelOrUndefined,
    getCountryEnLabelByCode,
    getCountryEnLabelByTranslatedLabel,
  };
};

export default useCountries;
